import { CommonModule, CurrencyPipe } from '@angular/common';
import { Component, ElementRef, ViewChild, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ApiService, showToast } from './api.service';

@Component({
  selector: 'app-customer-quote',
  standalone: true,
  imports: [CommonModule, FormsModule, CurrencyPipe],
  template: `
    <div class="min-h-screen bg-slate-50 p-4 flex justify-center">
      <main class="w-full max-w-3xl py-8">
        <div class="text-center mb-6">
          <img src="/logo.png" alt="NZ Plumbing" class="max-h-14 mx-auto mb-4">
          <p class="text-xs font-semibold text-slate-400 uppercase tracking-widest">Secure Quote Review</p>
        </div>

        @if (isLoading()) {
          <div class="bg-white rounded-2xl p-10 text-center border border-slate-100">
            <i class="ti ti-loader animate-spin text-4xl text-[#1A6FA6]"></i>
            <p class="mt-3 text-slate-500 font-medium">Loading quote...</p>
          </div>
        } @else if (error()) {
          <div class="bg-white rounded-2xl p-10 text-center border border-rose-100">
            <i class="ti ti-alert-triangle text-4xl text-rose-500"></i>
            <h1 class="text-xl font-semibold text-slate-900 mt-3">Quote Unavailable</h1>
            <p class="text-slate-500 mt-2">{{ error() }}</p>
          </div>
        } @else if (quote()) {
          <section class="bg-white rounded-2xl border border-slate-100 overflow-hidden">
            <div class="p-6 border-b border-slate-100">
              <div class="flex flex-wrap justify-between gap-4">
                <div>
                  <p class="text-xs font-semibold text-slate-400 uppercase tracking-widest">{{ quote().quote_no }} · Revision {{ quote().revision || 1 }}</p>
                  <h1 class="text-2xl font-semibold text-slate-900 mt-1">{{ quote().customer_name }}</h1>
                  <p class="text-sm text-slate-500 mt-1">{{ quote().job_address }}</p>
                </div>
                <div class="text-right">
                  <p class="text-xs text-slate-400 font-semibold uppercase">Total</p>
                  <p class="text-3xl font-semibold text-[#14384F]">{{ quote().total | currency:'NZD' }}</p>
                  <p class="text-xs text-slate-400">Valid until {{ quote().valid_until || 'not specified' }}</p>
                  <a [href]="publicPdfUrl()" target="_blank" rel="noopener" class="mt-3 inline-flex h-9 px-3 rounded-lg bg-[#EAF5FA] border border-[#D5EBF6] text-[#1A6FA6] text-xs font-semibold items-center gap-2 no-underline">
                    <i class="ti ti-file-type-pdf"></i> View PDF
                  </a>
                </div>
              </div>
            </div>

            <div class="p-6 space-y-3">
              @for (item of quote().items || []; track item.id || $index) {
                <div class="flex gap-3 rounded-2xl border border-slate-100 bg-slate-50/60 p-3">
                  <img [src]="item.photo_url || defaultPhoto" class="w-14 h-14 rounded-xl object-cover bg-white border border-slate-100" alt="">
                  <div class="flex-1 min-w-0">
                    <p class="font-semibold text-slate-900">{{ item.title }}</p>
                    @if (item.description) { <p class="text-sm text-slate-500 mt-0.5">{{ item.description }}</p> }
                    <p class="text-xs text-slate-400 mt-1">{{ item.quantity }} {{ item.unit }} × {{ item.unit_price | currency:'NZD' }}</p>
                  </div>
                  <p class="font-semibold text-slate-900">{{ item.total_price | currency:'NZD' }}</p>
                </div>
              }
            </div>

            @if (!quote().can_respond || quote().status === 'Accepted' || quote().status === 'Declined') {
              <div class="m-6 rounded-2xl p-5 text-center" [class.bg-emerald-50]="quote().status === 'Accepted'" [class.bg-rose-50]="quote().status === 'Declined'">
                <p class="font-semibold" [class.text-emerald-700]="quote().status === 'Accepted'" [class.text-rose-700]="quote().status === 'Declined'">{{ quote().response_message || 'This quote is not open for response.' }}</p>
                @if (quote().declined_reason) { <p class="text-sm text-rose-600 mt-1">{{ quote().declined_reason }}</p> }
              </div>
            } @else {
              <div class="p-6 border-t border-slate-100 space-y-5">
                @if (mode() === 'accept') {
                  <div>
                    <p class="text-sm font-semibold text-slate-700 mb-2">Digital Signature</p>
                    <div class="h-40 rounded-2xl border border-slate-200 bg-slate-50 relative overflow-hidden">
                      <canvas #signatureCanvas class="w-full h-full touch-none"></canvas>
                      @if (!hasSignature()) {
                        <div class="absolute inset-0 flex items-center justify-center pointer-events-none text-slate-300 font-semibold">Sign here</div>
                      }
                    </div>
                    <button (click)="clearSignature()" class="mt-2 text-xs font-semibold text-slate-400">Clear signature</button>
                  </div>
                }
                @if (mode() === 'decline') {
                  <textarea [(ngModel)]="declineReason" rows="4" placeholder="Please tell us why this quote is not going ahead."
                    class="w-full rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm outline-none focus:border-[#1A6FA6]"></textarea>
                }
                <div class="grid grid-cols-2 gap-3">
                  <button (click)="mode.set('decline')" class="h-12 rounded-xl border border-slate-200 bg-white text-slate-600 font-semibold">Decline</button>
                  <button (click)="mode.set('accept')" class="h-12 rounded-xl bg-[#14384F] text-white font-semibold">Accept</button>
                </div>
                <button (click)="submitResponse()" [disabled]="isSaving()" class="w-full h-12 rounded-xl bg-[#1A6FA6] text-white font-semibold disabled:opacity-60">
                  @if (isSaving()) { Submitting... } @else { Submit {{ mode() === 'accept' ? 'Acceptance' : 'Decline Reason' }} }
                </button>
              </div>
            }
          </section>
        }
      </main>
    </div>
  `
})
export class CustomerQuoteComponent {
  private api = inject(ApiService);
  private route = inject(ActivatedRoute);
  quote = signal<any>(null);
  isLoading = signal(true);
  isSaving = signal(false);
  error = signal('');
  mode = signal<'accept' | 'decline'>('accept');
  hasSignature = signal(false);
  declineReason = '';
  defaultPhoto = 'https://nzplumbinghq.co.nz/api/assets/default_mat.jpeg';
  private token = '';
  private ctx: CanvasRenderingContext2D | null = null;
  private drawing = false;

  @ViewChild('signatureCanvas') set canvasRef(ref: ElementRef<HTMLCanvasElement> | undefined) {
    if (!ref) return;
    setTimeout(() => this.setupCanvas(ref.nativeElement), 50);
  }
  private canvas?: HTMLCanvasElement;

  ngOnInit() {
    this.token = this.route.snapshot.paramMap.get('token') || '';
    this.loadQuote();
  }

  loadQuote() {
    this.api.getPublicQuote(this.token).subscribe({
      next: (res: any) => {
        this.isLoading.set(false);
        if (res.success) this.quote.set(res.data.quote);
        else this.error.set(res.message || 'Unable to load quote.');
      },
      error: () => {
        this.isLoading.set(false);
        this.error.set('Unable to load quote.');
      }
    });
  }

  setupCanvas(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const rect = canvas.getBoundingClientRect();
    const ratio = window.devicePixelRatio || 1;
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    this.ctx = canvas.getContext('2d');
    this.ctx?.setTransform(ratio, 0, 0, ratio, 0, 0);
    if (this.ctx) {
      this.ctx.lineWidth = 3;
      this.ctx.lineCap = 'round';
      this.ctx.strokeStyle = '#14384F';
    }
    canvas.onmousedown = e => this.start(e);
    canvas.onmousemove = e => this.move(e);
    canvas.onmouseup = () => this.stop();
    canvas.onmouseleave = () => this.stop();
    canvas.ontouchstart = e => this.start(e);
    canvas.ontouchmove = e => this.move(e);
    canvas.ontouchend = () => this.stop();
  }

  private pos(e: any) {
    const rect = this.canvas!.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: p.clientX - rect.left, y: p.clientY - rect.top };
  }
  private start(e: any) { e.preventDefault(); this.drawing = true; this.hasSignature.set(true); const p = this.pos(e); this.ctx?.beginPath(); this.ctx?.moveTo(p.x, p.y); }
  private move(e: any) { if (!this.drawing) return; e.preventDefault(); const p = this.pos(e); this.ctx?.lineTo(p.x, p.y); this.ctx?.stroke(); }
  private stop() { this.drawing = false; }
  clearSignature() { if (this.canvas) this.ctx?.clearRect(0, 0, this.canvas.width, this.canvas.height); this.hasSignature.set(false); }

  publicPdfUrl(): string {
    return `${this.api.baseUrl}/quotes/pdf.php?token=${encodeURIComponent(this.token)}`;
  }

  submitResponse() {
    if (this.mode() === 'accept' && (!this.canvas || !this.hasSignature())) {
      showToast('error', 'Please sign before accepting.');
      return;
    }
    if (this.mode() === 'decline' && !this.declineReason.trim()) {
      showToast('error', 'Please provide a decline reason.');
      return;
    }
    this.isSaving.set(true);
    this.api.respondToPublicQuote({
      token: this.token,
      action: this.mode(),
      signature_data: this.mode() === 'accept' ? this.canvas!.toDataURL('image/png') : undefined,
      reason: this.mode() === 'decline' ? this.declineReason.trim() : undefined
    }).subscribe({
      next: (res: any) => {
        this.isSaving.set(false);
        if (res.success) this.loadQuote();
        else showToast('error', res.message || 'Response failed.');
      },
      error: (err: any) => {
        this.isSaving.set(false);
        showToast('error', err?.error?.message || 'Network error submitting response.');
      }
    });
  }
}
